// src/ManagedTable/ManagedTable.tsx
import Table from "@cloudscape-design/components/table";

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/use-collection.js
import { useRef as useRef6, useMemo as useMemo3 } from "react";

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/operations/filter.js
function defaultFilteringFunction(item, filteringText, filteringFields) {
  if (filteringText.length === 0) {
    return true;
  }
  filteringFields = filteringFields || Object.keys(item);
  const lowFilteringText = filteringText.toLowerCase();
  return filteringFields.some((key) => {
    const value = item[key];
    if (value && typeof value === "object") {
      return false;
    }
    return String(value).toLowerCase().indexOf(lowFilteringText) > -1;
  });
}
function createFilterPredicate(filtering, filteringText = "") {
  var _a2;
  if (!filtering) {
    return null;
  }
  const filteringFunction = (_a2 = filtering.filteringFunction) !== null && _a2 !== void 0 ? _a2 : defaultFilteringFunction;
  return (item) => filteringFunction(item, filteringText, filtering.fields);
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/date-utils/parse-iso-date.js
function parseIsoDate(isoDate) {
  if (typeof isoDate === "string") {
    if (isoDate.includes("T")) {
      return new Date(isoDate);
    } else {
      return /* @__PURE__ */ new Date(isoDate + "T00:00:00");
    }
  }
  return /* @__PURE__ */ new Date(NaN);
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/date-utils/compare-dates.js
function compareDates(date, dateToCompare) {
  if (date instanceof Date && typeof dateToCompare === "string") {
    return startOfDay(date).getTime() - startOfDay(parseIsoDate(dateToCompare)).getTime();
  }
  return NaN;
}
function compareTimestamps(date, dateToCompare) {
  if (date instanceof Date && typeof dateToCompare === "string") {
    return date.getTime() - parseIsoDate(dateToCompare).getTime();
  }
  return NaN;
}
function startOfDay(date) {
  const copy = new Date(date.getTime());
  copy.setHours(0, 0, 0, 0);
  return copy;
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/logging.js
var isDevelopment = true;
var messageCache = /* @__PURE__ */ new Set();
function warnOnce(message) {
  if (isDevelopment) {
    const warning = `[AwsUi] collection-hooks ${message}`;
    if (!messageCache.has(warning)) {
      messageCache.add(warning);
      console.warn(warning);
    }
  }
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/operations/property-filter.js
var filterUsingOperator = (itemValue, { tokenValue, operator: { operator, match, tokenType } }) => {
  if (match === "date" || match === "datetime") {
    return matchDateValue({ tokenValue, itemValue, operator, match });
  } else if (typeof match === "function") {
    return match(itemValue, tokenValue);
  } else if (match) {
    throw new Error("Unsupported `operator.match` type given.");
  }
  return matchPrimitiveValue({ tokenValue, itemValue, operator, tokenType });
};
function matchDateValue({ tokenValue, itemValue, operator, match }) {
  const comparator = match === "date" ? compareDates : compareTimestamps;
  const comparisonResult = comparator(itemValue, tokenValue);
  switch (operator) {
    case "<":
      return comparisonResult < 0;
    case "<=":
      return comparisonResult <= 0;
    case ">":
      return comparisonResult > 0;
    case ">=":
      return comparisonResult >= 0;
    case "=":
      return comparisonResult === 0;
    case "!=":
      return comparisonResult !== 0;
    default:
      warnOnce(`Unsupported operator "${operator}" given for match="${match}".`);
      return false;
  }
}
function matchPrimitiveValue({ tokenValue, itemValue, operator, tokenType }) {
  if (tokenType === "enum") {
    if (!tokenValue || !Array.isArray(tokenValue)) {
      warnOnce('The token value must be an array when tokenType=="enum".');
      return false;
    }
    switch (operator) {
      case "=":
        return tokenValue && tokenValue.includes(itemValue);
      case "!=":
        return !tokenValue || !tokenValue.includes(itemValue);
      default:
        warnOnce(`Unsupported operator "${operator}" given for tokenType=="enum".`);
        return false;
    }
  }
  switch (operator) {
    case "<":
      return itemValue < tokenValue;
    case "<=":
      return itemValue <= tokenValue;
    case ">":
      return itemValue > tokenValue;
    case ">=":
      return itemValue >= tokenValue;
    case "=":
      return itemValue == tokenValue;
    case "!=":
      return itemValue != tokenValue;
    case ":":
      return (itemValue + "").toLowerCase().indexOf((tokenValue + "").toLowerCase()) > -1;
    case "!:":
      return (itemValue + "").toLowerCase().indexOf((tokenValue + "").toLowerCase()) === -1;
    case "^":
      return (itemValue + "").toLowerCase().startsWith((tokenValue + "").toLowerCase());
    case "!^":
      return !(itemValue + "").toLowerCase().startsWith((tokenValue + "").toLowerCase());
    default:
      throw new Error("Unsupported operator given.");
  }
}
function freeTextFilter(tokenValue, item, operator, filteringPropertiesMap, freeTextMatchMap) {
  const customMatch = freeTextMatchMap[operator];
  if (customMatch) {
    return customMatch(item, tokenValue);
  }
  const isNegation = operator.startsWith("!");
  return Object.keys(filteringPropertiesMap)[isNegation ? "every" : "some"]((propertyKey) => {
    const { operators } = filteringPropertiesMap[propertyKey];
    const propertyOperator = operators[operator];
    if (!propertyOperator) {
      return isNegation;
    }
    return filterUsingOperator(item[propertyKey], { tokenValue, operator: propertyOperator });
  });
}
function filterByToken(token, item, filteringPropertiesMap, freeTextMatchMap) {
  if (token.propertyKey) {
    if (!(token.propertyKey in filteringPropertiesMap) || !(token.operator in filteringPropertiesMap[token.propertyKey].operators)) {
      return false;
    }
    const property = filteringPropertiesMap[token.propertyKey];
    const operator = property.operators[token.operator];
    const itemValue = (operator === null || operator === void 0 ? void 0 : operator.match) ? item[token.propertyKey] : fixupFalsyValues(item[token.propertyKey]);
    return filterUsingOperator(itemValue, {
      tokenValue: token.value,
      operator: operator !== null && operator !== void 0 ? operator : { operator: token.operator }
    });
  }
  return freeTextFilter(token.value, item, token.operator, filteringPropertiesMap, freeTextMatchMap);
}
function isPropertyFilterTokenGroup(t) {
  const key = "operation";
  return key in t;
}
function defaultFilteringFunction2({ filteringProperties, freeTextFiltering }) {
  const evaluate = makeEvaluate(filteringProperties, freeTextFiltering);
  return (item, query) => {
    var _a2;
    return evaluate(item, { operation: query.operation, tokens: (_a2 = query.tokenGroups) !== null && _a2 !== void 0 ? _a2 : query.tokens });
  };
}
function makeEvaluate(filteringProperties, freeTextFiltering) {
  var _a2;
  const filteringPropertiesMap = filteringProperties.reduce((acc, { key, operators, defaultOperator }) => {
    const operatorMap = { [defaultOperator !== null && defaultOperator !== void 0 ? defaultOperator : "="]: { operator: defaultOperator !== null && defaultOperator !== void 0 ? defaultOperator : "=" } };
    operators === null || operators === void 0 ? void 0 : operators.forEach((op) => {
      if (typeof op === "string") {
        operatorMap[op] = { operator: op };
      } else {
        operatorMap[op.operator] = { operator: op.operator, match: op.match, tokenType: op.tokenType };
      }
    });
    acc[key] = { operators: operatorMap };
    return acc;
  }, {});
  const freeTextMatchMap = {};
  (_a2 = freeTextFiltering === null || freeTextFiltering === void 0 ? void 0 : freeTextFiltering.operators) === null || _a2 === void 0 ? void 0 : _a2.forEach((op) => {
    if (typeof op !== "string" && op.match) {
      freeTextMatchMap[op.operator] = op.match;
    }
  });
  return function evaluate(item, tokenOrGroup) {
    if (isPropertyFilterTokenGroup(tokenOrGroup)) {
      let result = tokenOrGroup.operation === "and" ? true : !tokenOrGroup.tokens.length;
      for (const group of tokenOrGroup.tokens) {
        result = tokenOrGroup.operation === "and" ? result && evaluate(item, group) : result || evaluate(item, group);
      }
      return result;
    } else {
      return filterByToken(tokenOrGroup, item, filteringPropertiesMap, freeTextMatchMap);
    }
  };
}
function createPropertyFilterPredicate(propertyFiltering, query = { tokens: [], operation: "and" }) {
  var _a2;
  if (!propertyFiltering) {
    return null;
  }
  const filteringFunction = (_a2 = propertyFiltering.filteringFunction) !== null && _a2 !== void 0 ? _a2 : defaultFilteringFunction2(propertyFiltering);
  return (item) => filteringFunction(item, query);
}
var fixupFalsyValues = (value) => {
  if (typeof value === "boolean") {
    return value + "";
  }
  if (value || value === 0) {
    return value;
  }
  return "";
};

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/operations/sort.js
function getSorter(sortingField) {
  if (!sortingField) {
    return null;
  }
  return (row1, row2) => {
    var _a2, _b;
    const value1 = (_a2 = row1[sortingField]) !== null && _a2 !== void 0 ? _a2 : "";
    const value2 = (_b = row2[sortingField]) !== null && _b !== void 0 ? _b : "";
    if (typeof value1 === "string" && typeof value2 === "string") {
      return value1.localeCompare(value2);
    }
    return value1 < value2 ? -1 : value1 == value2 ? 0 : 1;
  };
}
function createComparator(sorting, state) {
  var _a2;
  if (!sorting || !state) {
    return null;
  }
  const direction = state.isDescending ? -1 : 1;
  const comparator = (_a2 = state.sortingColumn.sortingComparator) !== null && _a2 !== void 0 ? _a2 : getSorter(state.sortingColumn.sortingField);
  return comparator ? (a, b) => comparator(a, b) * direction : null;
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/operations/pagination.js
var DEFAULT_PAGE_SIZE = 10;
function createPageProps(pagination, currentPageIndex, items) {
  var _a2;
  if (!pagination) {
    return null;
  }
  const pageSize = (_a2 = pagination.pageSize) !== null && _a2 !== void 0 ? _a2 : DEFAULT_PAGE_SIZE;
  const pagesCount = Math.ceil(items.length / pageSize);
  let pageIndex = currentPageIndex !== null && currentPageIndex !== void 0 ? currentPageIndex : 1;
  if (pageIndex < 1 || pageIndex > pagesCount && !pagination.allowPageOutOfRange || Number.isNaN(pageIndex)) {
    pageIndex = 1;
  }
  return { pageSize, pagesCount, pageIndex };
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/operations/compose-filters.js
function composeFilters(...predicates) {
  return predicates.some(Boolean) ? (item) => {
    for (const predicate of predicates) {
      if (predicate && !predicate(item)) {
        return false;
      }
    }
    return true;
  } : null;
}

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/base-component/component-metadata.js
import { useEffect, useRef } from "react";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/base-component/component-metrics.js
import { useEffect as useEffect2 } from "react";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/container-queries/use-resize-observer.js
import { unstable_batchedUpdates } from "react-dom";
import { useEffect as useEffect4, useLayoutEffect } from "react";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/stable-callback/index.js
import { useCallback, useEffect as useEffect3, useRef as useRef2 } from "react";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/singleton-handler/index.js
import { useEffect as useEffect5, useState } from "react";
import { unstable_batchedUpdates as unstable_batchedUpdates2 } from "react-dom";
function createSingletonHandler(factory) {
  const listeners = /* @__PURE__ */ new Set();
  const callback = (value) => {
    unstable_batchedUpdates2(() => {
      for (const listener of listeners) {
        listener(value);
      }
    });
  };
  let cleanup;
  return function useSingleton(listener) {
    useEffect5(() => {
      if (listeners.size === 0) {
        cleanup = factory(callback);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          cleanup();
          cleanup = void 0;
        }
      };
    }, []);
  };
}

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/visual-mode/index.js
import { useEffect as useEffect6, useState as useState2 } from "react";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/dom/element-types.js
function isNode(target) {
  return target instanceof Node || target !== null && typeof target === "object" && "nodeType" in target && typeof target.nodeType === "number" && "nodeName" in target && typeof target.nodeName === "string" && "parentNode" in target && typeof target.parentNode === "object";
}
function isHTMLElement(target) {
  return target instanceof HTMLElement || isNode(target) && target.nodeType === Node.ELEMENT_NODE && "style" in target && typeof target.style === "object" && typeof target.ownerDocument === "object" && !isSVGElement(target);
}
function isSVGElement(target) {
  return target instanceof SVGElement || isNode(target) && target.nodeType === Node.ELEMENT_NODE && "ownerSVGElement" in target && typeof target.ownerSVGElement === "object";
}

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/dom/find-up-until.js
function findUpUntil(from, test) {
  let current = from;
  while (current && !test(current)) {
    current = current.parentElement;
    while (current && !isHTMLElement(current)) {
      current = current.parentElement;
    }
  }
  return current;
}

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/dom/node-contains.js
function nodeContains(parent, descendant) {
  if (!parent || !descendant || !isNode(descendant)) {
    return false;
  }
  return parent.contains(descendant);
}

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/dom/node-belongs.js
function nodeBelongs(container, target) {
  var _a2;
  if (!isNode(target)) {
    return false;
  }
  const portal = findUpUntil(target, (node) => node === container || isHTMLElement(node) && !!node.dataset.awsuiReferrerId);
  if (portal && portal === container) {
    return true;
  }
  const referrer = isHTMLElement(portal) ? document.getElementById((_a2 = portal.dataset.awsuiReferrerId) !== null && _a2 !== void 0 ? _a2 : "") : null;
  return referrer ? nodeContains(container, referrer) : nodeContains(container, target);
}

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/global-flags/index.js
var awsuiVisualRefreshFlag = Symbol.for("awsui-visual-refresh-flag");
var awsuiGlobalFlagsSymbol = Symbol.for("awsui-global-flags");

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/visual-mode/index.js
var useMutationSingleton = createSingletonHandler((handler) => {
  const observer = new MutationObserver(() => handler());
  observer.observe(document.body, { attributes: true, subtree: true });
  return () => observer.disconnect();
});

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/focus-visible/index.js
import { useEffect as useEffect7 } from "react";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/keycode.js
var KeyCode;
(function(KeyCode2) {
  KeyCode2[KeyCode2["pageUp"] = 33] = "pageUp";
  KeyCode2[KeyCode2["pageDown"] = 34] = "pageDown";
  KeyCode2[KeyCode2["end"] = 35] = "end";
  KeyCode2[KeyCode2["home"] = 36] = "home";
  KeyCode2[KeyCode2["backspace"] = 8] = "backspace";
  KeyCode2[KeyCode2["space"] = 32] = "space";
  KeyCode2[KeyCode2["down"] = 40] = "down";
  KeyCode2[KeyCode2["left"] = 37] = "left";
  KeyCode2[KeyCode2["right"] = 39] = "right";
  KeyCode2[KeyCode2["up"] = 38] = "up";
  KeyCode2[KeyCode2["escape"] = 27] = "escape";
  KeyCode2[KeyCode2["enter"] = 13] = "enter";
  KeyCode2[KeyCode2["tab"] = 9] = "tab";
  KeyCode2[KeyCode2["shift"] = 16] = "shift";
  KeyCode2[KeyCode2["control"] = 17] = "control";
  KeyCode2[KeyCode2["alt"] = 18] = "alt";
  KeyCode2[KeyCode2["meta"] = 91] = "meta";
})(KeyCode || (KeyCode = {}));

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/single-tab-stop/index.js
import React, { createContext, forwardRef, useContext, useImperativeHandle, useLayoutEffect as useLayoutEffect2, useRef as useRef4, useState as useState3 } from "react";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/use-effect-on-update/index.js
import { useEffect as useEffect8, useRef as useRef3 } from "react";
function useEffectOnUpdate(callback, deps) {
  const isFirstRender = useRef3(true);
  useEffect8(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      return callback();
    }
  }, deps);
}

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/single-tab-stop/index.js
var defaultValue = {
  navigationActive: false,
  registerFocusable: () => () => {
  },
  resetFocusTarget: () => {
  }
};
var SingleTabStopNavigationContext = createContext(defaultValue);
var SingleTabStopNavigationProvider = forwardRef(({ navigationActive, children, getNextFocusTarget, isElementSuppressed, onRegisterFocusable, onUnregisterActive }, ref) => {
  const focusables = useRef4(/* @__PURE__ */ new Set());
  const focusHandlers = useRef4(/* @__PURE__ */ new Map());
  const focusablesState = useRef4(/* @__PURE__ */ new WeakMap());
  const focusTarget = useRef4(null);
  function onUnregisterFocusable(focusableElement) {
    const isUnregisteringFocusedNode = nodeBelongs(focusableElement, document.activeElement);
    if (isUnregisteringFocusedNode) {
      setTimeout(() => onUnregisterActive === null || onUnregisterActive === void 0 ? void 0 : onUnregisterActive(focusableElement), 0);
    }
  }
  function registerFocusable(focusableElement, changeHandler) {
    const parentUnregister = parentContext.registerFocusable(focusableElement, changeHandler);
    focusables.current.add(focusableElement);
    focusHandlers.current.set(focusableElement, changeHandler);
    const isFocusable2 = !!focusablesState.current.get(focusableElement);
    const newIsFocusable = focusTarget.current === focusableElement || !!(isElementSuppressed === null || isElementSuppressed === void 0 ? void 0 : isElementSuppressed(focusableElement));
    if (newIsFocusable !== isFocusable2) {
      focusablesState.current.set(focusableElement, newIsFocusable);
      changeHandler(newIsFocusable);
    }
    onRegisterFocusable === null || onRegisterFocusable === void 0 ? void 0 : onRegisterFocusable(focusableElement);
    return () => {
      parentUnregister();
      unregisterFocusable(focusableElement);
    };
  }
  function unregisterFocusable(focusableElement) {
    focusables.current.delete(focusableElement);
    focusHandlers.current.delete(focusableElement);
    onUnregisterFocusable === null || onUnregisterFocusable === void 0 ? void 0 : onUnregisterFocusable(focusableElement);
  }
  function updateFocusTarget(forceUpdate = false) {
    var _a2;
    focusTarget.current = getNextFocusTarget();
    for (const focusableElement of focusables.current) {
      const isFocusable2 = (_a2 = focusablesState.current.get(focusableElement)) !== null && _a2 !== void 0 ? _a2 : false;
      const newIsFocusable = focusTarget.current === focusableElement || !!(isElementSuppressed === null || isElementSuppressed === void 0 ? void 0 : isElementSuppressed(focusableElement));
      if (newIsFocusable !== isFocusable2 || forceUpdate) {
        focusablesState.current.set(focusableElement, newIsFocusable);
        focusHandlers.current.get(focusableElement)(newIsFocusable);
      }
    }
  }
  function resetFocusTarget() {
    updateFocusTarget(true);
  }
  function getFocusTarget() {
    return focusTarget.current;
  }
  function isRegistered(element) {
    return focusables.current.has(element);
  }
  useImperativeHandle(ref, () => ({ updateFocusTarget, getFocusTarget, isRegistered }));
  const parentContext = useContext(SingleTabStopNavigationContext);
  const value = parentContext.navigationActive ? parentContext : { navigationReset: false, navigationActive, registerFocusable, updateFocusTarget, resetFocusTarget };
  useEffectOnUpdate(() => {
    if (parentContext.navigationActive) {
      parentContext.resetFocusTarget();
    } else {
      resetFocusTarget();
    }
  }, [parentContext.navigationActive]);
  return React.createElement(SingleTabStopNavigationContext.Provider, { value }, children);
});

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/focus-lock-utils/utils.js
var tabbables = [
  "button:enabled",
  "select:enabled",
  "textarea:enabled",
  "input:enabled",
  "a[href]",
  "area[href]",
  "summary",
  "iframe",
  "object",
  "embed",
  "audio[controls]",
  "video[controls]",
  "[tabindex]",
  "[contenteditable]",
  "[autofocus]"
].join(",");

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/portal/index.js
import React2, { useLayoutEffect as useLayoutEffect3, useState as useState4 } from "react";
import { createPortal } from "react-dom";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/use-merge-refs/index.js
import { useMemo } from "react";

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/use-unique-id/index.js
import React3, { useRef as useRef5 } from "react";
var _a;
var counter = 0;
var useRandomId = () => {
  const idRef = useRef5(null);
  if (!idRef.current) {
    idRef.current = `${counter++}-${Date.now()}-${Math.round(Math.random() * 1e4)}`;
  }
  return idRef.current;
};
var useId = (_a = React3.useId) !== null && _a !== void 0 ? _a : useRandomId;

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/track-by/index.js
var getTrackableValue = (trackBy, item) => {
  if (!trackBy) {
    return item;
  }
  if (typeof trackBy === "function") {
    return trackBy(item);
  }
  return item[trackBy];
};

// ../../node_modules/@amzn/awsui-component-toolkit/mjs/internal/selection-tree/index.js
var ROOT_KEY = Symbol("selection-tree-root");
var SelectionTree = class _SelectionTree {
  constructor(roots, treeProps, state) {
    this.itemKeyToItem = /* @__PURE__ */ new Map();
    this.itemToggleState = /* @__PURE__ */ new Set();
    this.itemProjectedSelectionState = /* @__PURE__ */ new Set();
    this.itemProjectedParentSelectionState = /* @__PURE__ */ new Set();
    this.itemProjectedIndeterminateState = /* @__PURE__ */ new Set();
    this.itemKeyToSelectedCount = /* @__PURE__ */ new Map();
    this.selectedItems = new Array();
    this.isItemSelected = (item) => this.itemProjectedSelectionState.has(this.getKey(item));
    this.isItemIndeterminate = (item) => this.itemProjectedIndeterminateState.has(this.getKey(item));
    this.isAllItemsSelected = () => this.itemProjectedSelectionState.has(ROOT_KEY) && !this.itemProjectedIndeterminateState.has(ROOT_KEY);
    this.isAllItemsIndeterminate = () => this.itemProjectedIndeterminateState.has(ROOT_KEY);
    this.getSelectedItemsCount = (item) => {
      var _a2;
      return (_a2 = this.itemKeyToSelectedCount.get(this.getKey(item))) !== null && _a2 !== void 0 ? _a2 : 0;
    };
    this.getSelectedItems = () => this.selectedItems;
    this.getState = () => {
      const inverted = this.itemToggleState.has(ROOT_KEY);
      const toggledItems = [];
      for (const itemKey of Array.from(this.itemToggleState)) {
        const item = this.getItemForKey(itemKey);
        if (item) {
          toggledItems.push(item);
        }
      }
      return { inverted, toggledItems };
    };
    this.toggleAll = () => {
      return this.isAllItemsSelected() ? new _SelectionTree(this.roots, this.treeProps, { inverted: false, toggledItems: [] }) : new _SelectionTree(this.roots, this.treeProps, { inverted: true, toggledItems: [] });
    };
    this.toggleSome = (requestedItems) => {
      const clone = this.clone();
      const lastItemKey = clone.getKey(requestedItems[requestedItems.length - 1]);
      const isParentSelected = clone.itemProjectedParentSelectionState.has(lastItemKey);
      const isSelected = clone.itemProjectedSelectionState.has(lastItemKey);
      const isIndeterminate = clone.itemProjectedIndeterminateState.has(lastItemKey);
      const nextIsSelected = !(isSelected && !isIndeterminate);
      const nextIsSelfSelected = isParentSelected && !nextIsSelected || !isParentSelected && nextIsSelected;
      for (const requested of requestedItems) {
        clone.unselectDeep(requested);
        if (nextIsSelfSelected) {
          clone.itemToggleState.add(this.getKey(requested));
        }
      }
      clone.computeState();
      return clone;
    };
    this.invertAll = () => {
      const clone = this.clone();
      clone.toggleKey(ROOT_KEY);
      clone.roots.forEach((item) => clone.toggleKey(clone.getKey(item)));
      clone.computeState();
      return clone;
    };
    this.invertOne = (item) => {
      const clone = this.clone();
      clone.toggleKey(clone.getKey(item));
      clone.treeProps.getChildren(item).forEach((child) => clone.toggleKey(clone.getKey(child)));
      clone.computeState();
      return clone;
    };
    this.toggleKey = (key) => {
      if (this.itemToggleState.has(key)) {
        this.itemToggleState.delete(key);
      } else {
        this.itemToggleState.add(key);
      }
    };
    this.unselectDeep = (item) => {
      this.itemToggleState.delete(this.getKey(item));
      this.treeProps.getChildren(item).forEach((child) => this.unselectDeep(child));
    };
    this.roots = roots;
    this.treeProps = treeProps;
    if (state.inverted) {
      this.itemToggleState.add(ROOT_KEY);
    }
    for (const item of state.toggledItems) {
      this.itemToggleState.add(this.getKey(item));
    }
    const traverse = (item) => {
      this.itemKeyToItem.set(this.getKey(item), item);
      treeProps.getChildren(item).forEach(traverse);
    };
    roots.forEach(traverse);
    this.computeState();
  }
  computeState() {
    var _a2, _b;
    this.itemProjectedSelectionState = /* @__PURE__ */ new Set();
    this.itemProjectedIndeterminateState = /* @__PURE__ */ new Set();
    this.itemProjectedParentSelectionState = /* @__PURE__ */ new Set();
    this.itemKeyToSelectedCount = /* @__PURE__ */ new Map();
    this.selectedItems = [];
    const selectionBuckets = /* @__PURE__ */ new Map();
    const createSelectionBuckets = (item, level) => {
      var _a3;
      const itemKey = this.getKey(item);
      const levelBuckets = (_a3 = selectionBuckets.get(level)) !== null && _a3 !== void 0 ? _a3 : [];
      const children = this.treeProps.getChildren(item);
      const bucket = [itemKey];
      for (const child of children) {
        bucket.push(this.getKey(child));
        createSelectionBuckets(child, level + 1);
      }
      levelBuckets.push(bucket);
      selectionBuckets.set(level, levelBuckets);
    };
    const rootBucket = [ROOT_KEY];
    for (const item of this.roots) {
      rootBucket.push(this.getKey(item));
      createSelectionBuckets(item, 1);
    }
    selectionBuckets.set(0, [rootBucket]);
    const selectionBucketEntries = Array.from(selectionBuckets.entries()).sort(([a], [b]) => b - a).flatMap(([, v]) => v);
    for (const bucket of selectionBucketEntries) {
      if (bucket.length === 1) {
        continue;
      }
      let selectedCount = 0;
      for (let i = bucket.length - 1; i >= 0; i--) {
        if (this.itemToggleState.has(bucket[i])) {
          selectedCount++;
        } else {
          break;
        }
      }
      if (((_b = (_a2 = this.treeProps).isComplete) === null || _b === void 0 ? void 0 : _b.call(_a2, this.getItemForKey(bucket[0]))) === false) {
        continue;
      }
      if (selectedCount === bucket.length - 1 && !this.itemToggleState.has(bucket[0])) {
        bucket.forEach((itemKey) => this.itemToggleState.delete(itemKey));
        this.itemToggleState.add(bucket[0]);
      }
      if (selectedCount === bucket.length) {
        bucket.forEach((itemKey) => this.itemToggleState.delete(itemKey));
      }
    }
    const setItemProjectedSelection = (item, inheritedSelected) => {
      const itemKey = this.getKey(item);
      const isSelfSelected = this.itemToggleState.has(itemKey);
      const isSelected = isSelfSelected && !inheritedSelected || !isSelfSelected && inheritedSelected;
      if (isSelected) {
        this.itemProjectedSelectionState.add(itemKey);
      }
      if (inheritedSelected) {
        this.itemProjectedParentSelectionState.add(itemKey);
      }
      this.treeProps.getChildren(item).forEach((child) => setItemProjectedSelection(child, isSelected));
    };
    this.roots.forEach((item) => {
      const isRootSelected = this.itemToggleState.has(ROOT_KEY);
      if (isRootSelected) {
        this.itemProjectedSelectionState.add(ROOT_KEY);
      }
      setItemProjectedSelection(item, isRootSelected);
    });
    for (const bucket of selectionBucketEntries) {
      let indeterminate = false;
      for (let i = 1; i < bucket.length; i++) {
        if (this.itemToggleState.has(bucket[i]) || this.itemProjectedIndeterminateState.has(bucket[i])) {
          indeterminate = true;
          break;
        }
      }
      if (indeterminate) {
        this.itemProjectedIndeterminateState.add(bucket[0]);
      }
    }
    const computeCounts = (item) => {
      const children = this.treeProps.getChildren(item);
      const selfCount = !this.isGroup(item) && this.isItemSelected(item) ? 1 : 0;
      const count = selfCount + children.reduce((acc, child) => acc + computeCounts(child), 0);
      if (selfCount) {
        this.selectedItems.push(item);
      }
      this.itemKeyToSelectedCount.set(this.getKey(item), count);
      return count;
    };
    for (const item of this.roots) {
      computeCounts(item);
    }
  }
  // Presently, the implementation treats all nodes with children as groups. Groups do not
  // contribute to the counters and are not returned as selected items - only the non-group nodes do.
  // This can be made configurable by exposing the isGroup as property.
  isGroup(item) {
    return this.treeProps.getChildren(item).length > 0;
  }
  getKey(item) {
    return getTrackableValue(this.treeProps.trackBy, item);
  }
  getItemForKey(itemKey) {
    if (itemKey === ROOT_KEY) {
      return null;
    }
    return this.itemKeyToItem.get(itemKey);
  }
  clone() {
    return new _SelectionTree(this.roots, this.treeProps, this.getState());
  }
};

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/operations/items-tree.js
function computeFlatItems(items, filterPredicate, sortingComparator) {
  if (filterPredicate) {
    items = items.filter(filterPredicate);
  }
  if (sortingComparator) {
    items = items.slice().sort(sortingComparator);
  }
  return {
    items,
    rootItemsCount: items.length,
    selectableItemsCount: items.length,
    getItemChildren: void 0,
    isItemExpandable: void 0,
    getItemsCount: void 0
  };
}
function computeTreeItems(allItems, treeProps, filterPredicate, sortingComparator) {
  var _a2;
  const idToChildren = /* @__PURE__ */ new Map();
  const idToCount = /* @__PURE__ */ new Map();
  let items = [];
  let rootItemsCount = 0;
  let selectableItemsCount = 0;
  for (const item of allItems) {
    const parentId = treeProps.getParentId(item);
    if (parentId === null) {
      items.push(item);
    } else {
      const children = (_a2 = idToChildren.get(parentId)) !== null && _a2 !== void 0 ? _a2 : [];
      children.push(item);
      idToChildren.set(parentId, children);
    }
  }
  const getItemChildren = (item) => {
    var _a3;
    return (_a3 = idToChildren.get(treeProps.getId(item))) !== null && _a3 !== void 0 ? _a3 : [];
  };
  const setChildren = (item, children) => idToChildren.set(treeProps.getId(item), children);
  const isItemExpandable = (item) => {
    var _a3;
    return ((_a3 = getItemChildren(item)) === null || _a3 === void 0 ? void 0 : _a3.length) > 0;
  };
  if (filterPredicate) {
    const filterNode = (item) => {
      const children = getItemChildren(item);
      const filteredChildren = children.filter(filterNode);
      setChildren(item, filteredChildren);
      return filterPredicate(item) || filteredChildren.length > 0;
    };
    items = items.filter(filterNode);
  }
  if (sortingComparator) {
    const sortLevel = (levelItems) => {
      levelItems.sort(sortingComparator);
      for (const item of levelItems) {
        sortLevel(getItemChildren(item));
      }
    };
    sortLevel(items);
  }
  function computeSelectableCount(item) {
    const children = getItemChildren(item);
    let itemCount = children.length === 0 ? 1 : 0;
    for (const child of children) {
      itemCount += computeSelectableCount(child);
    }
    idToCount.set(treeProps.getId(item), itemCount);
    return itemCount;
  }
  for (const item of items) {
    rootItemsCount += 1;
    selectableItemsCount += computeSelectableCount(item);
  }
  const getItemsCount = treeProps.dataGrouping ? (item) => {
    var _a3;
    return (_a3 = idToCount.get(treeProps.getId(item))) !== null && _a3 !== void 0 ? _a3 : 0;
  } : void 0;
  return { items, rootItemsCount, selectableItemsCount, getItemChildren, isItemExpandable, getItemsCount };
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/operations/index.js
function processItems(allItems, state, { filtering, sorting, pagination, propertyFiltering, expandableRows, selection }) {
  var _a2, _b, _c, _d;
  const filterPredicate = composeFilters(createPropertyFilterPredicate(propertyFiltering, state.propertyFilteringQuery), createFilterPredicate(filtering, state.filteringText));
  const sortingComparator = createComparator(sorting, state.sortingState);
  const { items, rootItemsCount, selectableItemsCount, getItemChildren, isItemExpandable, getItemsCount } = expandableRows ? computeTreeItems(allItems, expandableRows, filterPredicate, sortingComparator) : computeFlatItems(allItems, filterPredicate, sortingComparator);
  const filteredItemsCount = filterPredicate ? rootItemsCount : void 0;
  let getSelectedItemsCount = void 0;
  let selectedItems = void 0;
  if (selection && (expandableRows === null || expandableRows === void 0 ? void 0 : expandableRows.dataGrouping) && state.groupSelection && getItemChildren) {
    const trackBy = (_a2 = selection === null || selection === void 0 ? void 0 : selection.trackBy) !== null && _a2 !== void 0 ? _a2 : expandableRows === null || expandableRows === void 0 ? void 0 : expandableRows.getId;
    const selectionTreeProps = { getChildren: getItemChildren, trackBy };
    const selectionTree = new SelectionTree(items, selectionTreeProps, state.groupSelection);
    selectedItems = selectionTree.getSelectedItems();
    getSelectedItemsCount = selectionTree.getSelectedItemsCount;
  }
  const expandableRowsResult = getItemChildren && {
    getItemChildren,
    isItemExpandable,
    getItemsCount,
    totalItemsCount: selectableItemsCount,
    getSelectedItemsCount,
    totalSelectedItemsCount: (_d = (_b = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) !== null && _b !== void 0 ? _b : (_c = state.selectedItems) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0
  };
  const pageProps = createPageProps(pagination, state.currentPageIndex, items);
  if (pageProps) {
    return {
      items: items.slice((pageProps.pageIndex - 1) * pageProps.pageSize, pageProps.pageIndex * pageProps.pageSize),
      allPageItems: items,
      totalItemsCount: rootItemsCount,
      filteredItemsCount,
      pagesCount: pageProps === null || pageProps === void 0 ? void 0 : pageProps.pagesCount,
      actualPageIndex: pageProps === null || pageProps === void 0 ? void 0 : pageProps.pageIndex,
      selectedItems,
      expandableRows: expandableRowsResult
    };
  }
  return {
    items,
    allPageItems: items,
    totalItemsCount: rootItemsCount,
    filteredItemsCount,
    pagesCount: void 0,
    actualPageIndex: void 0,
    selectedItems,
    expandableRows: expandableRowsResult
  };
}
var processSelectedItems = (items, selectedItems, trackBy) => {
  const selectedSet = /* @__PURE__ */ new Set();
  selectedItems.forEach((item) => selectedSet.add(getTrackableValue(trackBy, item)));
  return items.filter((item) => selectedSet.has(getTrackableValue(trackBy, item)));
};
var itemsAreEqual = (items1, items2, trackBy) => {
  if (items1.length !== items2.length) {
    return false;
  }
  const set1 = /* @__PURE__ */ new Set();
  items1.forEach((item) => set1.add(getTrackableValue(trackBy, item)));
  return items2.every((item) => set1.has(getTrackableValue(trackBy, item)));
};

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/utils.js
function computeFilteringOptions(allItems, filteringProperties) {
  if (!filteringProperties) {
    return [];
  }
  return filteringProperties.reduce((acc, property) => {
    const cache = /* @__PURE__ */ new Set([""]);
    const addOption = (value) => {
      if (!cache.has(value)) {
        cache.add(value);
        acc.push({ propertyKey: property.key, value });
        return true;
      }
      return false;
    };
    for (const item of allItems) {
      addOption("" + fixupFalsyValues(item[property.key]));
    }
    return acc;
  }, []);
}
function collectionReducer(state, action) {
  const newState = Object.assign({}, state);
  switch (action.type) {
    case "selection":
      newState.selectedItems = action.selectedItems;
      break;
    case "group-selection":
      newState.groupSelection = action.state;
      break;
    case "expansion":
      newState.expandedItems = action.expandedItems;
      break;
    case "filtering":
      newState.currentPageIndex = 1;
      newState.filteringText = action.filteringText;
      break;
    case "sorting":
      newState.currentPageIndex = 1;
      newState.sortingState = action.sortingState;
      break;
    case "pagination":
      newState.currentPageIndex = action.pageIndex;
      break;
    case "property-filtering":
      newState.currentPageIndex = 1;
      newState.propertyFilteringQuery = action.query;
      break;
  }
  return newState;
}
function createActions({ dispatch, collectionRef }) {
  return {
    setFiltering(filteringText) {
      var _a2;
      dispatch({ type: "filtering", filteringText });
      (_a2 = collectionRef.current) === null || _a2 === void 0 ? void 0 : _a2.scrollToTop();
    },
    setSorting(state) {
      var _a2;
      dispatch({ type: "sorting", sortingState: state });
      (_a2 = collectionRef.current) === null || _a2 === void 0 ? void 0 : _a2.scrollToTop();
    },
    setCurrentPage(pageIndex) {
      var _a2;
      dispatch({ type: "pagination", pageIndex });
      (_a2 = collectionRef.current) === null || _a2 === void 0 ? void 0 : _a2.scrollToTop();
    },
    setSelectedItems(selectedItems) {
      dispatch({ type: "selection", selectedItems });
    },
    setPropertyFiltering(query) {
      var _a2;
      dispatch({ type: "property-filtering", query });
      (_a2 = collectionRef.current) === null || _a2 === void 0 ? void 0 : _a2.scrollToTop();
    },
    setExpandedItems(expandedItems) {
      dispatch({ type: "expansion", expandedItems });
    },
    setGroupSelection(groupSelection) {
      dispatch({ type: "group-selection", state: groupSelection });
    }
  };
}
function createSyncProps(options, { filteringText, sortingState, selectedItems, expandedItems, currentPageIndex, propertyFilteringQuery, groupSelection }, actions, collectionRef, { pagesCount, actualPageIndex, allItems, totalItemsCount, expandableRows, filteringOptions }) {
  var _a2, _b, _c, _d, _e;
  let empty = options.filtering ? allItems.length ? options.filtering.noMatch : options.filtering.empty : null;
  empty = options.propertyFiltering ? allItems.length ? options.propertyFiltering.noMatch : options.propertyFiltering.empty : empty;
  return {
    collectionProps: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ empty }, options.sorting ? {
      onSortingChange: ({ detail }) => {
        actions.setSorting(detail);
      },
      sortingColumn: sortingState === null || sortingState === void 0 ? void 0 : sortingState.sortingColumn,
      sortingDescending: sortingState === null || sortingState === void 0 ? void 0 : sortingState.isDescending
    } : {}), options.expandableRows && expandableRows ? {
      expandableRows: Object.assign(Object.assign({}, expandableRows), {
        expandedItems,
        onExpandableItemToggle: ({ detail: { item, expanded } }) => {
          const getId = options.expandableRows.getId;
          if (expanded) {
            for (const stateItem of expandedItems) {
              if (getId(stateItem) === getId(item)) {
                return;
              }
            }
            actions.setExpandedItems([...expandedItems, item]);
          } else {
            actions.setExpandedItems(expandedItems.filter((stateItem) => getId(stateItem) !== getId(item)));
          }
        },
        // The table component uses group selection when expandableRows.groupSelection is defined. Therefore,
        // we only pass this property when selection and dataGrouping are configured in use-collection options.
        groupSelection: options.selection && options.expandableRows.dataGrouping ? groupSelection : void 0,
        onGroupSelectionChange: ({ detail }) => actions.setGroupSelection(detail.groupSelection)
      }),
      // The trackBy property is used to match expanded items by ID and not by object reference.
      // The property can be overridden by the explicitly provided selection.trackBy.
      // If that is the case, we assume both selection.trackBy and expandableRows.getId have the same result.
      // If not, the expandable state won't be matched correctly by the table.
      trackBy: options.expandableRows.getId
    } : {}), options.selection ? {
      onSelectionChange: ({ detail: { selectedItems: selectedItems2 } }) => {
        actions.setSelectedItems(selectedItems2);
      },
      selectedItems,
      trackBy: (_a2 = options.selection.trackBy) !== null && _a2 !== void 0 ? _a2 : (_b = options.expandableRows) === null || _b === void 0 ? void 0 : _b.getId
    } : {}), { ref: collectionRef, firstIndex: 1, totalItemsCount }), ((_c = options.pagination) === null || _c === void 0 ? void 0 : _c.pageSize) ? {
      firstIndex: ((actualPageIndex !== null && actualPageIndex !== void 0 ? actualPageIndex : currentPageIndex) - 1) * options.pagination.pageSize + 1
    } : {}),
    filterProps: {
      filteringText,
      onChange: ({ detail: { filteringText: filteringText2 } }) => {
        actions.setFiltering(filteringText2);
      }
    },
    propertyFilterProps: {
      query: propertyFilteringQuery,
      onChange: ({ detail: query }) => {
        actions.setPropertyFiltering(query);
      },
      filteringProperties: ((_d = options.propertyFiltering) === null || _d === void 0 ? void 0 : _d.filteringProperties) || [],
      filteringOptions,
      freeTextFiltering: (_e = options.propertyFiltering) === null || _e === void 0 ? void 0 : _e.freeTextFiltering
    },
    paginationProps: {
      currentPageIndex: actualPageIndex !== null && actualPageIndex !== void 0 ? actualPageIndex : currentPageIndex,
      // pagesCount is always calculated when options.pagination is present
      pagesCount,
      onChange: ({ detail: { currentPageIndex: currentPageIndex2 } }) => {
        actions.setCurrentPage(currentPageIndex2);
      }
    }
  };
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/use-collection-state.js
import { useReducer, useMemo as useMemo2 } from "react";
function useCollectionState(options, collectionRef) {
  var _a2, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
  const [state, dispatch] = useReducer(collectionReducer, {
    selectedItems: (_b = (_a2 = options.selection) === null || _a2 === void 0 ? void 0 : _a2.defaultSelectedItems) !== null && _b !== void 0 ? _b : [],
    expandedItems: (_d = (_c = options.expandableRows) === null || _c === void 0 ? void 0 : _c.defaultExpandedItems) !== null && _d !== void 0 ? _d : [],
    sortingState: (_e = options.sorting) === null || _e === void 0 ? void 0 : _e.defaultState,
    currentPageIndex: (_g = (_f = options.pagination) === null || _f === void 0 ? void 0 : _f.defaultPage) !== null && _g !== void 0 ? _g : 1,
    filteringText: (_j = (_h = options.filtering) === null || _h === void 0 ? void 0 : _h.defaultFilteringText) !== null && _j !== void 0 ? _j : "",
    propertyFilteringQuery: (_l = (_k = options.propertyFiltering) === null || _k === void 0 ? void 0 : _k.defaultQuery) !== null && _l !== void 0 ? _l : { tokens: [], operation: "and" },
    groupSelection: { inverted: false, toggledItems: (_o = (_m = options.selection) === null || _m === void 0 ? void 0 : _m.defaultSelectedItems) !== null && _o !== void 0 ? _o : [] }
  });
  const actions = useMemo2(() => createActions({ dispatch, collectionRef }), [dispatch, collectionRef]);
  return [state, actions];
}

// ../../node_modules/@amzn/awsui-collection-hooks/mjs/use-collection.js
function useCollection(allItems, options) {
  var _a2, _b;
  const collectionRef = useRef6(null);
  const [state, actions] = useCollectionState(options, collectionRef);
  const filteringProperties = (_a2 = options.propertyFiltering) === null || _a2 === void 0 ? void 0 : _a2.filteringProperties;
  const { items, allPageItems, pagesCount, totalItemsCount, filteredItemsCount, actualPageIndex, selectedItems, expandableRows } = processItems(allItems, state, options);
  const expandedItemsSet = /* @__PURE__ */ new Set();
  if (options.expandableRows) {
    for (const item of state.expandedItems) {
      expandedItemsSet.add(options.expandableRows.getId(item));
    }
  }
  let visibleItems = items;
  if (options.expandableRows) {
    const flatItems = new Array();
    const getId = options.expandableRows.getId;
    const traverse = (items2) => {
      for (const item of items2) {
        flatItems.push(item);
        if (expandableRows && expandedItemsSet.has(getId(item))) {
          traverse(expandableRows.getItemChildren(item));
        }
      }
    };
    traverse(items);
    visibleItems = flatItems;
  }
  if (options.selection && !options.selection.keepSelection) {
    const newSelectedItems = processSelectedItems(visibleItems, state.selectedItems, options.selection.trackBy);
    if (!itemsAreEqual(newSelectedItems, state.selectedItems, options.selection.trackBy)) {
      actions.setSelectedItems(newSelectedItems);
    }
  }
  if (options.expandableRows) {
    const newExpandedItems = visibleItems.filter((item) => expandedItemsSet.has(options.expandableRows.getId(item)));
    if (!itemsAreEqual(newExpandedItems, state.expandedItems, options.expandableRows.getId)) {
      actions.setExpandedItems(newExpandedItems);
    }
  }
  const externalFilteringOptions = (_b = options.propertyFiltering) === null || _b === void 0 ? void 0 : _b.filteringOptions;
  const filteringOptions = useMemo3(() => externalFilteringOptions !== null && externalFilteringOptions !== void 0 ? externalFilteringOptions : computeFilteringOptions(allItems, filteringProperties), [allItems, filteringProperties, externalFilteringOptions]);
  const extendedState = selectedItems ? Object.assign(Object.assign({}, state), { selectedItems }) : state;
  return Object.assign({
    items,
    allPageItems,
    filteredItemsCount,
    actions
  }, createSyncProps(options, extendedState, actions, collectionRef, {
    actualPageIndex,
    pagesCount,
    allItems,
    totalItemsCount,
    expandableRows,
    filteringOptions
  }));
}

// src/ManagedTable/ManagedTable.tsx
import Pagination from "@cloudscape-design/components/pagination";
import TextFilter from "@cloudscape-design/components/text-filter";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import { useMemo as useMemo4 } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var defaultPreferences = {
  pageSize: 10,
  wrapLines: true
};
var ManagedTable = function(props) {
  const staticPreferences = {
    ...defaultPreferences,
    ...props.defaultPreferences
  };
  const hasSorting = useMemo4(
    () => props.columnDefinitions.some((column) => column.sortingField || column.sortingComparator),
    [props.columnDefinitions]
  );
  const modifiedColumnDefinitions = useMemo4(() => {
    if (props.items.length <= 5) {
      return props.columnDefinitions.map(({ sortingComparator, sortingField, ...column }) => ({
        ...column
      }));
    }
    return props.columnDefinitions;
  }, [props.columnDefinitions, props.items.length]);
  const { filterProps, paginationProps, collectionProps, items, filteredItemsCount, actions } = useCollection(props.items, {
    ...props,
    sorting: hasSorting ? props.sorting ?? {} : void 0,
    filtering: {
      noMatch: /* @__PURE__ */ jsx(Box, { margin: { vertical: "xs" }, textAlign: "center", color: "inherit", children: /* @__PURE__ */ jsxs(SpaceBetween, { size: "m", children: [
        /* @__PURE__ */ jsx("b", { children: "No matches" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            onClick: () => {
              actions.setFiltering("");
            },
            children: "Clear filter"
          }
        )
      ] }) }),
      ...props.filtering
    },
    pagination: {
      ...props.pagination,
      pageSize: staticPreferences.pageSize
    }
  });
  return /* @__PURE__ */ jsx(
    Table,
    {
      wrapLines: staticPreferences.wrapLines,
      contentDensity: staticPreferences.contentDensity,
      stripedRows: staticPreferences.stripedRows,
      ...props,
      ...collectionProps,
      empty: props.empty || /* @__PURE__ */ jsxs(Box, { textAlign: "center", color: "inherit", children: [
        /* @__PURE__ */ jsx("b", { children: "No items" }),
        /* @__PURE__ */ jsx(Box, { variant: "p", color: "inherit", children: "No items to display." })
      ] }),
      columnDefinitions: modifiedColumnDefinitions,
      items,
      pagination: props.pagination && props.items?.length > 5 && /* @__PURE__ */ jsx(Pagination, { ...props.pagination, ...paginationProps }),
      filter: props.filtering && props.items?.length > 5 && /* @__PURE__ */ jsx(
        TextFilter,
        {
          ...props.filtering,
          countText: typeof filteredItemsCount === "number" ? filteredItemsCount === 1 ? "1 match" : `${filteredItemsCount} matches` : void 0,
          ...filterProps
        }
      )
    }
  );
};
export {
  ManagedTable
};
//# sourceMappingURL=index.js.map
